// Self-healing for the Twilio Device (the "zombie seat" fix — see
// plans/seat-health/device-zombie-diagnosis.md). A Device that wedges while
// its seat is in a pool takes every routed call down with it, and the router
// re-feeds the seat because each dead call frees it within a second. The only
// cure is the one a manual tab refresh performs: throw the Device away and
// build a fresh one. This module holds the pure decision logic — when a
// rebuild is warranted, and when it must NOT happen — so the policies are
// testable without a Device.
//
// Two hard rules the numbers encode:
// - Never rebuild on 31205 (routine token expiry): tokenWillExpire already
//   recovers it, and token expiry is fleet-wide — rebuilding on it would turn
//   every token roll into a synchronized rebuild storm.
// - Rebuilds are rate-limited and capped: a Device that cannot be revived by
//   five rebuilds in an hour needs a human (the deviceAlert banner), not a
//   sixth attempt.

export type RebuildReason =
  | 'error_31009' // TransportError: no transport to send/receive — terminal
  | 'error_20101' // AccessTokenInvalid on reconnect — the classic wedge
  | 'error_31005_repeated' // ConnectionError; transient alone, terminal in bursts
  | 'watchdog' // seated but not `registered` and register() didn't take
  | 'ice_crash'; // the SDK's unhandled null-peer-connection ICE restart

export const REBUILD_MIN_GAP_MS = 30_000;
export const REBUILD_CAP = 5;
export const REBUILD_CAP_WINDOW_MS = 60 * 60_000;

// 31005 fires for blips a healthy Device rides out; only a burst marks a wedge.
export const ERROR_31005_TRIP = 3;
export const ERROR_31005_WINDOW_MS = 5 * 60_000;

// The watchdog exists for the silent wedge (no error events at all — the
// Device just sits unregistered while the page runs on). Registering takes
// ~1s normally; 30s of not-registered while seated is not a blip. One
// register() gets a chance first: if the token is still valid it's the
// cheapest possible recovery. Only when that didn't take do we rebuild.
export const WATCHDOG_TICK_MS = 10_000;
export const WATCHDOG_REGISTER_AFTER_MS = 30_000;
export const WATCHDOG_REBUILD_AFTER_MS = 45_000;

// Collapse the burst of wake-ish events (visibilitychange + pageshow + online
// can all fire for one lid-open) into a single token refresh.
export const WAKE_REFRESH_MIN_GAP_MS = 10_000;

export const REBUILD_CAP_ALERT =
  'Your dialer keeps losing its connection and automatic recovery has given up — ' +
  'refresh the page (Cmd+R / F5) to get a fresh phone.';

const prune = (times: number[], windowMs: number, now: number): number[] =>
  times.filter((t) => t > now - windowMs && t <= now);

// Terminal device-error codes → rebuild reason; everything else (31205
// included, deliberately) → null. 31005 is NOT here: it needs the burst
// ledger below, a single one is survivable.
export const terminalErrorReason = (code: number | null): RebuildReason | null => {
  if (code === 31009) return 'error_31009';
  if (code === 20101) return 'error_20101';
  return null;
};

// Burst ledger for 31005. Returns the pruned ledger and whether this one
// tripped the threshold; a trip drains the ledger so the next 31005 starts a
// fresh burst instead of re-tripping immediately.
export const record31005 = (
  times: number[],
  now: number,
): { times: number[]; tripped: boolean } => {
  const kept = [...prune(times, ERROR_31005_WINDOW_MS, now), now];
  if (kept.length >= ERROR_31005_TRIP) return { times: [], tripped: true };
  return { times: kept, tripped: false };
};

// The rebuild gate. `history` is the timestamps of past rebuilds. The returned
// history is safe to store on every outcome: it only gains `now` when `allow`
// is true, so denied attempts never consume budget. `capped` means the hour
// budget is spent — the caller raises the banner and stops trying.
export const decideRebuild = (
  history: number[],
  now: number,
): { allow: boolean; capped: boolean; history: number[] } => {
  const kept = prune(history, REBUILD_CAP_WINDOW_MS, now);
  if (kept.length >= REBUILD_CAP) return { allow: false, capped: true, history: kept };
  const last = kept[kept.length - 1];
  if (last !== undefined && now - last < REBUILD_MIN_GAP_MS)
    return { allow: false, capped: false, history: kept };
  return { allow: true, capped: false, history: [...kept, now] };
};

// The crash family we hunt (twilio-voice.js#99, open since 2022): an ICE
// restart against a peer connection that no longer exists. Browsers word the
// null-access TypeError differently — Chromium "Cannot read properties of
// null (reading 'createOffer')", WebKit "null is not an object (evaluating
// '….createOffer')" — both naming the property in the MESSAGE; Firefox
// ("this._x is null") names it only in the STACK. So: a message naming
// createOffer matches (with the stack, when one exists, confirming the SDK
// path); a message that doesn't needs the stack to show the restart chain
// itself (createOffer AND iceRestart frames — which also catches the sibling
// null-'sdp' crash, twilio-voice.js#100, same dead-media outcome). Stack
// matching relies on terser keeping method names, its default — a future
// mangle-props change would quietly disable this trigger, not break it; the
// state watchdog remains the backstop either way.
export const matchesIceRestartCrash = (message: unknown, stack: unknown): boolean => {
  const msg = typeof message === 'string' ? message : '';
  const stk = typeof stack === 'string' ? stack : '';
  // The message branch requires the null-access wording, not just the method
  // name: "Failed to execute 'createOffer' on 'RTCPeerConnection'" is an
  // ordinary negotiation failure on a live peer connection, and rebuilding on
  // it would kill a healthy phone. The dead-peer crash always reads null-ish.
  if (msg.includes('createOffer') && (msg.includes('null') || msg.includes('undefined')))
    return stk.length === 0 || stk.includes('iceRestart') || stk.includes('createOffer');
  return stk.includes('createOffer') && stk.includes('iceRestart');
};

// A deferred rebuild carries REQUIREMENTS, not just a label. Merging two
// deferrals must preserve both hard properties: non-cancellability (an
// ice_crash parked under a later watchdog trigger must not become droppable
// on `registered`) and the fresh-token wait (a 20101 parked under a later
// ice_crash must not rebuild with the credential Twilio just rejected — the
// combined reconnect+crash sequence produces exactly this pair). The label
// keeps the strongest reason for telemetry; the flags OR/AND respectively.
export interface PendingRebuild {
  reason: RebuildReason;
  freshToken: boolean; // must wait for a CHANGED token before building
  cancellable: boolean; // may be dropped on finding the device `registered`
}

export const pendingFor = (reason: RebuildReason): PendingRebuild => ({
  reason,
  freshToken: reason === 'error_20101',
  cancellable: cancellableOnRegistered(reason),
});

export const mergePending = (
  current: PendingRebuild | null,
  incoming: RebuildReason,
): PendingRebuild => {
  const next = pendingFor(incoming);
  if (current === null) return next;
  const rank = (r: RebuildReason): number => (r === 'ice_crash' ? 2 : r === 'error_20101' ? 1 : 0);
  return {
    reason: rank(next.reason) > rank(current.reason) ? next.reason : current.reason,
    freshToken: current.freshToken || next.freshToken,
    cancellable: current.cancellable && next.cancellable,
  };
};

// Backoff for re-asking the BFF for a token while none is usable. The base
// matches the watchdog cadence; the ceiling keeps a fleet of tabs facing a
// persistent token:null (an unmapped SDR, a sustained core mint outage, the
// intentional local simulated-device path) from hammering the BFF every 30s
// forever. Reset the moment a real token lands.
export const TOKEN_REASK_BASE_MS = 30_000;
export const TOKEN_REASK_MAX_MS = 10 * 60_000;

// Whether a DEFERRED rebuild (parked while a call was live or the tab was
// seatless) may be cancelled on finding the Device `registered`. Registration
// only attests SIGNALLING — and the diagnosed zombie is exactly "signalling
// recovered, media permanently dead" (diagnosis §4). So the ICE crash, whose
// entire failure mode is a dead media layer under live signalling, must
// survive the registered check; the state- and transport-shaped reasons are
// cancellable because for them registration IS the recovery.
export const cancellableOnRegistered = (reason: RebuildReason): boolean => reason !== 'ice_crash';

// One watchdog tick. `state` is the Device's registration state; anything but
// 'registered' while seated and idle counts as a breach. The sequence per
// breach is: wait → one register() attempt → wait → rebuild. A live call or a
// registered Device resets everything (a call in progress means media works —
// interrupting it would be the bug we're fixing, inverted).
export interface WatchdogState {
  breachAt: number | null;
  registerTriedAt: number | null;
}

export const watchdogStep = (
  deviceState: string,
  s: WatchdogState,
  now: number,
): { next: WatchdogState; action: 'none' | 'register' | 'rebuild' } => {
  if (deviceState === 'registered')
    return { next: { breachAt: null, registerTriedAt: null }, action: 'none' };
  if (s.breachAt === null)
    return { next: { breachAt: now, registerTriedAt: null }, action: 'none' };
  const elapsed = now - s.breachAt;
  if (s.registerTriedAt === null) {
    if (elapsed >= WATCHDOG_REGISTER_AFTER_MS)
      return { next: { ...s, registerTriedAt: now }, action: 'register' };
    return { next: s, action: 'none' };
  }
  if (elapsed >= WATCHDOG_REBUILD_AFTER_MS)
    return { next: { breachAt: null, registerTriedAt: null }, action: 'rebuild' };
  return { next: s, action: 'none' };
};
